import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, Check, X, Clock, Coffee, Loader2, Lock, Plus,
  TrendingUp, TrendingDown, HandCoins, Trash2, History, Save, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import DateFieldPT from "@/components/DateFieldPT";

// Status de presença (mesmas labels do backend: presente/meia/falta/folga)
const STATUS_OPTIONS = [
  { k: "presente", label: "Presente", icon: Check,  tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { k: "meia",     label: "Meia",     icon: Clock,  tone: "bg-amber-100 text-amber-700 border-amber-200" },
  { k: "falta",    label: "Falta",    icon: X,      tone: "bg-rose-100 text-rose-700 border-rose-200" },
  { k: "folga",    label: "Folga",    icon: Coffee, tone: "bg-slate-100 text-slate-600 border-slate-200" },
];
const nextStatusAfter = (cur) => {
  const i = STATUS_OPTIONS.findIndex((s) => s.k === cur);
  return STATUS_OPTIONS[(i + 1) % STATUS_OPTIONS.length].k;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

function daysBetween(start, end) {
  const d1 = new Date(start + "T00:00:00");
  const d2 = new Date(end + "T00:00:00");
  const days = [];
  const cur = new Date(d1);
  while (cur <= d2) {
    days.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

// --- Extra/Vale/Desconto dialog -------------------------------------
const EXTRA_CATS = {
  extra:    [{ v: "hora_extra", l: "Hora extra" }, { v: "bonus", l: "Bônus" }, { v: "avulso", l: "Serviço avulso" }],
  vale:     [{ v: "adiantamento", l: "Adiantamento" }],
  desconto: [{ v: "material", l: "Material perdido" }, { v: "atraso", l: "Atraso" }, { v: "outro", l: "Outro" }],
};
const EXTRA_TONE = {
  extra:    { icon: TrendingUp,  color: "text-emerald-600", bg: "bg-emerald-500 hover:bg-emerald-600", label: "Extra" },
  vale:     { icon: HandCoins,   color: "text-amber-600",   bg: "bg-amber-500 hover:bg-amber-600",     label: "Vale" },
  desconto: { icon: TrendingDown,color: "text-rose-600",    bg: "bg-rose-500 hover:bg-rose-600",       label: "Desconto" },
};

function LancamentoDialog({ open, onOpenChange, emp, type, onSaved }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayIso());
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const cats = EXTRA_CATS[type] || [];
  const tone = EXTRA_TONE[type] || EXTRA_TONE.extra;

  useEffect(() => {
    if (open) {
      setAmount("");
      setDate(todayIso());
      setCategory(cats[0]?.v || "");
      setNote("");
    }
  }, [open, type]); // eslint-disable-line

  const submit = async () => {
    const v = parseFloat(String(amount).replace(",", "."));
    if (!(v > 0)) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await api.post("/payroll/extras", {
        employee_id: emp.id,
        date,
        type,
        category,
        amount: v,
        note,
      });
      toast.success(`${tone.label} lançado para ${emp.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <tone.icon className={`w-5 h-5 ${tone.color}`} />
            {tone.label} · {emp?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-700 uppercase">Valor</label>
            <Input
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="input-extra-amount"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700 uppercase">Data</label>
            <DateFieldPT value={date} onChange={setDate} testid="input-extra-date" fromYear={2026} />
          </div>
          {cats.length > 1 && (
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase">Categoria</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="input-extra-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[11px] font-semibold text-slate-700 uppercase">Observação (opcional)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-extra-note" />
          </div>
        </div>
        <DialogFooter className="flex-row gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={`text-white ${tone.bg}`} onClick={submit} disabled={saving} data-testid="btn-extra-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salvar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Period selector -----------------------------------------------
const PRESETS = [
  { k: "since_last", label: "Desde último fechamento" },
  { k: "7d",         label: "Últimos 7 dias" },
  { k: "15d",        label: "Últimos 15 dias" },
  { k: "month",      label: "Mês atual" },
  { k: "custom",     label: "Personalizado" },
];

export default function Diarias() {
  const [employees, setEmployees] = useState([]);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayIso());
  const [preset, setPreset] = useState("month");
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState({}); // key `empId:date` → status
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [lancDialog, setLancDialog] = useState({ open: false, emp: null, type: null });
  const [expanded, setExpanded] = useState(null); // employee_id

  const applyPreset = async (p) => {
    setPreset(p);
    const today = todayIso();
    if (p === "since_last") {
      try {
        const r = await api.get("/payroll/last-closing-end");
        const last = r.data?.end;
        setStart(last ? addDays(last, 1) : "2026-10-01");
        setEnd(today);
      } catch { setStart("2026-10-01"); setEnd(today); }
    } else if (p === "7d") { setStart(addDays(today, -6)); setEnd(today); }
    else if (p === "15d") { setStart(addDays(today, -14)); setEnd(today); }
    else if (p === "month") { setStart(monthStart()); setEnd(today); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [emp, sum] = await Promise.all([
        api.get("/employees?active_only=true"),
        api.get(`/payroll/summary?start=${start}&end=${end}`),
      ]);
      setEmployees(emp.data || []);
      setSummary(sum.data);
      // Pull attendance for the period
      const att = {};
      for (const r of sum.data.rows) {
        try {
          const a = await api.get(`/attendance?start=${start}&end=${end}&employee_id=${r.employee_id}`);
          for (const e of a.data?.entries || []) att[`${e.employee_id}:${e.date}`] = e.status;
        } catch { /* ignore per-emp attendance error */ }
      }
      setAttendance(att);
    } catch (e) {
      toast.error("Falha ao carregar folha");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [start, end]); // eslint-disable-line

  const days = useMemo(() => daysBetween(start, end), [start, end]);

  const setStatus = async (empId, date, nextStatus) => {
    const key = `${empId}:${date}`;
    const prev = attendance[key];
    if (prev === nextStatus) return;
    setAttendance((a) => ({ ...a, [key]: nextStatus }));
    try {
      await api.post("/attendance", { employee_id: empId, date, status: nextStatus });
      const sum = await api.get(`/payroll/summary?start=${start}&end=${end}`);
      setSummary(sum.data);
    } catch (e) {
      toast.error("Falha ao salvar");
      setAttendance((a) => ({ ...a, [key]: prev }));
    }
  };

  const removeExtra = async (id) => {
    if (!window.confirm("Remover este lançamento?")) return;
    try {
      await api.delete(`/payroll/extras/${id}`);
      toast.success("Lançamento removido");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao remover"); }
  };

  const payOne = async (r) => {
    if (!window.confirm(`Pagar ${r.name}: ${BRL(r.net)} referente a ${formatDateBR(start)} → ${formatDateBR(end)}?`)) return;
    setClosing(true);
    try {
      await api.post("/payroll/close", { start, end, employee_ids: [r.employee_id] });
      toast.success(`${r.name} pago · ${BRL(r.net)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no pagamento");
    } finally { setClosing(false); }
  };

  const closePeriod = async () => {
    if (!summary) return;
    const msg = `Fechar o período ${formatDateBR(start)} → ${formatDateBR(end)} e gerar ${BRL(summary.total)} em pagamentos?`;
    if (!window.confirm(msg)) return;
    setClosing(true);
    try {
      const r = await api.post("/payroll/close", { start, end });
      toast.success(`${r.data.expenses_created} pagamento(s) gerado(s) · ${BRL(r.data.total)}`);
      // Avança o período pra "próximo dia → hoje"
      setStart(addDays(end, 1));
      setEnd(todayIso());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no fechamento");
    } finally { setClosing(false); }
  };

  return (
    <div className="space-y-5 pb-20">
      <PageHeader
        icon={CalendarDays}
        title="Folha de pagamento"
        subtitle="Período livre · extras · vales · descontos"
        iconColor="bg-amber-500"
        right={
          <Link
            to="/funcionarios/historico"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#7c3aed] hover:underline"
            data-testid="link-payroll-history"
          >
            <History className="w-4 h-4" /> Histórico
          </Link>
        }
      />

      {/* Period picker */}
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => applyPreset(p.k)}
              className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors ${
                preset === p.k
                  ? "bg-[#7c3aed] text-white border-[#7c3aed]"
                  : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
              }`}
              data-testid={`preset-${p.k}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase">De</label>
            <DateFieldPT value={start} onChange={(v) => { setStart(v); setPreset("custom"); }} testid="period-start" fromYear={2026} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase">Até</label>
            <DateFieldPT value={end} onChange={(v) => { setEnd(v); setPreset("custom"); }} testid="period-end" fromYear={2026} />
          </div>
        </div>
      </div>

      {/* Total + Close */}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">A pagar</p>
          <p className="font-heading text-2xl font-black text-[#1e3a8a] leading-none mt-0.5" data-testid="period-total">
            {BRL(summary?.total || 0)}
          </p>
        </div>
        <Button
          onClick={closePeriod}
          disabled={closing || !summary || summary.total <= 0}
          className="h-14 px-5 bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-bold shadow-md"
          data-testid="btn-close-period"
        >
          {closing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-5 h-5 mr-1" /> Pagar todos</>}
        </Button>
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#7c3aed]" /></div>}

      {/* Employees list */}
      {!loading && summary?.rows?.map((r) => {
        const isExpanded = expanded === r.employee_id;
        const emp = { id: r.employee_id, name: r.name };
        return (
          <div key={r.employee_id} className="bg-white rounded-2xl border border-slate-100 card-shadow overflow-hidden" data-testid={`employee-row-${r.employee_id}`}>
            {/* Header row */}
            <button
              type="button"
              onClick={() => setExpanded(isExpanded ? null : r.employee_id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="text-left min-w-0">
                <p className="font-heading font-black text-[#1e3a8a] leading-tight truncate">{r.name}</p>
                <p className="text-[11px] text-slate-500">{r.role || "—"} · {BRL(r.daily_rate)}/dia</p>
                <div className="flex gap-2 mt-1 text-[10px] font-semibold">
                  <span className="text-emerald-700">{r.present}P</span>
                  <span className="text-amber-700">{r.half}½</span>
                  <span className="text-rose-700">{r.absent}F</span>
                  <span className="text-slate-500">{r.folga}□</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading text-lg font-black text-[#7c3aed] leading-none">{BRL(r.net)}</p>
                {(r.extras_total > 0 || r.vales_total > 0 || r.descontos_total > 0) && (
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {BRL(r.gross)}
                    {r.extras_total > 0 && <> +{BRL(r.extras_total)}</>}
                    {r.vales_total > 0 && <> −{BRL(r.vales_total)}</>}
                    {r.descontos_total > 0 && <> −{BRL(r.descontos_total)}</>}
                  </p>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 inline-block ml-1 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-3">
                {r.paid_ranges?.length > 0 && (
                  <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5" data-testid={`paid-ranges-${r.employee_id}`}>
                    Já pago: {r.paid_ranges.map(([s, e]) => `${formatDateBR(s)} → ${formatDateBR(e)}`).join(", ")}
                  </p>
                )}
                <Button
                  onClick={() => payOne(r)}
                  disabled={closing || !(r.net > 0)}
                  className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid={`btn-pay-one-${r.employee_id}`}
                >
                  <Lock className="w-4 h-4 mr-1" /> Pagar só {r.name.split(" ")[0]} · {BRL(r.net)}
                </Button>
                {/* Attendance grid */}
                <div>
                  <p className="text-[11px] font-bold uppercase text-slate-500 tracking-wider mb-2">Presença</p>
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((d) => {
                      const st = attendance[`${r.employee_id}:${d}`];
                      const meta = STATUS_OPTIONS.find((s) => s.k === st);
                      const dd = d.slice(8, 10);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setStatus(r.employee_id, d, nextStatusAfter(st || "falta"))}
                          className={`aspect-square rounded-md border text-[10px] font-bold flex flex-col items-center justify-center transition-colors ${
                            meta ? meta.tone : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"
                          }`}
                          title={`${d} · ${meta?.label || "Sem registro"}`}
                          data-testid={`cell-${r.employee_id}-${d}`}
                        >
                          <span className="leading-none">{dd}</span>
                          {meta && <meta.icon className="w-2.5 h-2.5 mt-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-slate-500">
                    <div className="flex gap-2 flex-wrap">
                      {STATUS_OPTIONS.map((s) => (
                        <span key={s.k} className={`px-1.5 py-0.5 rounded ${s.tone}`}>{s.label}</span>
                      ))}
                    </div>
                    <span className="font-mono">(toque p/ alternar)</span>
                  </div>
                </div>

                {/* Lançamentos (extras/vales/descontos) */}
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px]"
                    onClick={() => setLancDialog({ open: true, emp, type: "extra" })} data-testid={`btn-add-extra-${r.employee_id}`}>
                    <TrendingUp className="w-3 h-3 mr-1" /> Extra
                  </Button>
                  <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-600 text-white text-[11px]"
                    onClick={() => setLancDialog({ open: true, emp, type: "vale" })} data-testid={`btn-add-vale-${r.employee_id}`}>
                    <HandCoins className="w-3 h-3 mr-1" /> Vale
                  </Button>
                  <Button size="sm" className="h-8 bg-rose-500 hover:bg-rose-600 text-white text-[11px]"
                    onClick={() => setLancDialog({ open: true, emp, type: "desconto" })} data-testid={`btn-add-desconto-${r.employee_id}`}>
                    <TrendingDown className="w-3 h-3 mr-1" /> Desconto
                  </Button>
                  <Link
                    to={`/funcionarios/historico?employee_id=${r.employee_id}`}
                    className="inline-flex items-center h-8 px-3 rounded-md bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700"
                    data-testid={`btn-history-${r.employee_id}`}
                  >
                    <History className="w-3 h-3 mr-1" /> Histórico
                  </Link>
                </div>

                {r.extras_items?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-bold uppercase text-slate-500 tracking-wider mb-1.5">Lançamentos do período</p>
                    <div className="space-y-1">
                      {r.extras_items.map((x) => {
                        const tone = EXTRA_TONE[x.type];
                        return (
                          <div key={x.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-100">
                            <tone.icon className={`w-3.5 h-3.5 ${tone.color} shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-800 leading-tight">
                                {tone.label} · {BRL(x.amount)}
                                {x.category && <span className="font-normal text-slate-500"> · {x.category}</span>}
                              </p>
                              <p className="text-[10px] text-slate-500 truncate">
                                {formatDateBR(x.date)}{x.note ? ` · ${x.note}` : ""}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExtra(x.id)}
                              className="text-slate-400 hover:text-rose-600 transition-colors shrink-0"
                              title="Remover"
                              data-testid={`btn-remove-extra-${x.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!loading && !summary?.rows?.length && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
          <CalendarDays className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Nenhum funcionário ativo. Cadastre em "Funcionários".</p>
        </div>
      )}

      <LancamentoDialog
        open={lancDialog.open}
        onOpenChange={(o) => setLancDialog((s) => ({ ...s, open: o }))}
        emp={lancDialog.emp}
        type={lancDialog.type}
        onSaved={load}
      />
    </div>
  );
}
